import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, History, PackageSearch, Recycle } from "lucide-react";
import type { ReactNode } from "react";

import { COLORS, TrendChart } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import {
  LcAgeCohorts,
  buildAgeCohorts,
  countPastThreshold,
  medianAgeYears,
  totalProvisioned,
} from "@/components/LcAgeCohorts";
import { LcAgeRiskMatrix } from "@/components/LcAgeRiskMatrix";
import { LcReplacementAge } from "@/components/LcReplacementAge";
import { LcReplacementFlow } from "@/components/LcReplacementFlow";
import { MetricLabel } from "@/components/MetricTooltip";
import { PageHeader } from "@/components/PageHeader";
import { useScope } from "@/context/ScopeContext";
import { apiClient } from "@/lib/api-client";
import { formatInt, formatPct } from "@/lib/format";
import { scopeDisplayName } from "@/lib/scope";

// /api/lifecycle is served widened server-side (existing LifecyclePayload + the R5 `replacementAge`
// block — see apps/hardware-hub-api/src/services/replacement-age.ts). `useLifecycle` in queries.ts
// still types its return as the lean `LifecycleResponse` (untouched, per iteration-D's additive
// convention), so this page reads the SAME endpoint through the widened response type rather than
// duplicating fetch logic. Same query key as `useLifecycle` so TanStack dedupes the network call.
const useLifecycleWithReplacementAge = () =>
  useQuery({
    queryKey: ["lifecycle"] as const,
    queryFn: apiClient.getLifecycleWithReplacementAge,
  });

// Lifecycle — provisioning history → age cohorts, a REAL age×risk crosstab (roster joined to latest
// risk tier), and the real replacement/EOL demand signal. Only the forward "replacement-ahead" KPI
// remains explicitly modeled.

// Cohort key whose at-risk share peaks (called out in the InsightCallout).
const PEAK_RISK_COHORT = "2to3y";

// Typical tablet service life used for the past-threshold cohort tail + KPI.
const SERVICE_LIFE_YEARS = 4;
// Cohort index at which devices are past service life (matches LcAgeCohorts COHORT_DEFS ordering).
const PAST_LIFE_COHORT_INDEX = 4;
// Days per year, for converting the replacement-age proxy (avgAgeDays) into years for the KPI.
const DAYS_PER_YEAR = 365.25;

export const LifecyclePage = (): ReactNode => {
  const { data, isLoading, isError, error } = useLifecycleWithReplacementAge();
  const { scope } = useScope();
  const meta = data?.meta;
  const payload = data?.data;

  const cohorts = payload ? buildAgeCohorts(payload.provisionedByMonth) : [];
  const p50 = payload ? medianAgeYears(payload.provisionedByMonth) : null;
  const pastThreshold = payload
    ? countPastThreshold(payload.provisionedByMonth, SERVICE_LIFE_YEARS)
    : 0;
  const fleetProvisioned = payload ? totalProvisioned(payload.provisionedByMonth) : 0;
  const crosstab = payload?.ageRiskCrosstab ?? null;
  // R5 — replacement-age (replacement-duration proxy). Additive block; undefined until the endpoint
  // widening reaches this deploy, so every read below stays optional and degrades to an EmptyState.
  const replacementAge = payload?.replacementAge ?? null;
  const replacementAgeYears =
    replacementAge?.retiredAvgAgeDays != null
      ? replacementAge.retiredAvgAgeDays / DAYS_PER_YEAR
      : null;

  const peakCohort = crosstab?.cohorts.find((c) => c.cohort === PEAK_RISK_COHORT) ?? null;

  if (isError) {
    return (
      <>
        <PageHeader title="Lifecycle" meta={meta} />
        <ChartCard title="Lifecycle">
          <EmptyState
            title="Couldn't load lifecycle"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Lifecycle"
        description={`Provisioning history, fleet age cohorts, and replacement / EOL demand. Scope: ${scopeDisplayName(scope)} — every number drills into its dataset.`}
        meta={meta}
      />

      {peakCohort ? (
        <InsightCallout
          headline={`At-risk share peaks in the ${peakCohort.label} cohort at ${formatPct(peakCohort.atRiskPct)} — the highest of any age band.`}
          stats={[
            { label: `${peakCohort.label} at-risk`, value: formatPct(peakCohort.atRiskPct) },
            { label: `${peakCohort.label} devices`, value: formatInt(peakCohort.total) },
          ]}
          severity="warn"
          cta={{ label: "Open Fleet Health", to: "/health" }}
        />
      ) : null}

      <div className="grid kpi-band">
        <Drillable metricKey="device.ageDays" scope={scope} label="Drill into device age">
          <KpiCard
            label={<MetricLabel metricKey="device.ageDays" label="Fleet age p50" />}
            value={p50 === null ? "—" : `${p50.toFixed(1)}y`}
            icon={History}
            source="lifecycle.provisionedByMonth"
            loading={isLoading}
          />
        </Drillable>
        <Drillable
          metricKey="device.ageDays"
          scope={scope}
          label="Drill into devices past service life"
        >
          <KpiCard
            label={
              <MetricLabel
                metricKey="device.ageDays"
                label={`Past ${SERVICE_LIFE_YEARS}-year service life`}
              />
            }
            value={formatInt(pastThreshold)}
            icon={CalendarClock}
            source="lifecycle.provisionedByMonth"
            loading={isLoading}
          />
        </Drillable>
        <Drillable
          metricKey="device.replacementRatePct"
          scope={scope}
          label="Drill into replacement signal"
        >
          <KpiCard
            label={
              <MetricLabel
                metricKey="device.replacementRatePct"
                label="Replacement-ahead (modeled)"
              />
            }
            value={formatInt(payload?.replacementOrders ?? 0)}
            icon={PackageSearch}
            source="lifecycle.replacementOrders · modeled"
            loading={isLoading}
          />
        </Drillable>
        <Drillable
          metricKey="device.ageDays"
          scope={scope}
          label="Drill into replacement-age (duration proxy)"
        >
          <KpiCard
            label={
              <MetricLabel metricKey="device.ageDays" label="Replacement age (proxy, ~2.7y)" />
            }
            value={replacementAgeYears === null ? "—" : `${replacementAgeYears.toFixed(1)}y`}
            icon={Recycle}
            source="lifecycle.replacementAge · inferred from retired-unit age"
            loading={isLoading}
          />
        </Drillable>
      </div>

      <div className="grid cols-3">
        <ChartCard
          title="Provisioned by month"
          subtitle="Devices first provisioned per month — the real intake history that ages into cohorts"
          source="lifecycle.provisionedByMonth"
          loading={isLoading}
          className="span-2"
        >
          {payload && payload.provisionedByMonth.length > 0 ? (
            <Drillable
              metricKey="device.provisionedOn"
              scope={scope}
              label="Drill into devices by provisioning month"
            >
              <TrendChart data={payload.provisionedByMonth} height={220} color={COLORS.accent} />
            </Drillable>
          ) : (
            <EmptyState title="No provisioning history" />
          )}
        </ChartCard>

        <ChartCard
          title="Age cohorts"
          subtitle={`Fleet-wide by age · past-${SERVICE_LIFE_YEARS}y tail accented`}
          source="lifecycle.provisionedByMonth"
          loading={isLoading}
        >
          {cohorts.length > 0 && fleetProvisioned > 0 ? (
            <LcAgeCohorts cohorts={cohorts} atRiskFromIndex={PAST_LIFE_COHORT_INDEX} />
          ) : (
            <EmptyState title="No cohort data" />
          )}
        </ChartCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Age × risk crosstab"
          subtitle="REAL device counts — provisioning-age cohort joined to each device's latest risk tier"
          source="lifecycle.ageRiskCrosstab (roster ⋈ rpt_hw_device_latest)"
          loading={isLoading}
        >
          {crosstab ? (
            <Drillable
              metricKey="health.composite"
              scope={scope}
              label="Drill into the devices behind this age × risk crosstab"
            >
              <LcAgeRiskMatrix crosstab={crosstab} />
            </Drillable>
          ) : (
            <EmptyState title="No crosstab data" message="No scored devices matched the roster." />
          )}
        </ChartCard>

        <ChartCard
          title="Replacement & EOL signal"
          subtitle="Real demand: replacement orders + returned / restocked / scrapped"
          source="lifecycle (returns + RMA dispositions)"
          loading={isLoading}
          className="span-2"
        >
          {payload ? (
            <Drillable
              metricKey="device.replacementRatePct"
              scope={scope}
              label="Drill into replacement-rate detail"
            >
              <LcReplacementFlow
                signal={{
                  replacementOrders: payload.replacementOrders,
                  returnedUnits: payload.returnedUnits,
                  restockedUnits: payload.restockedUnits,
                  scrappedUnits: payload.scrappedUnits,
                }}
              />
            </Drillable>
          ) : (
            <EmptyState title="No replacement data" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Replacement age (duration proxy)"
          subtitle="How long the OUTGOING unit was in service — age-at-retirement, by model × cohort"
          source="lifecycle.replacementAge (rpt_lifecycle_replacement_age)"
          loading={isLoading}
        >
          {replacementAge ? (
            <Drillable
              metricKey="device.ageDays"
              scope={scope}
              label="Drill into the devices behind the replacement-age distribution"
            >
              <LcReplacementAge payload={replacementAge} />
            </Drillable>
          ) : (
            <EmptyState
              title="No replacement-age data"
              message="E2 orders carry no serial, so a per-order duration isn't available — this proxy needs the retired-unit age join."
            />
          )}
        </ChartCard>
      </div>
    </>
  );
};
